import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';

import './RecordedVideoCreate.css'

import SelectionDropdown from '../../Common/SelectionDropdown';
import SelectionDropdownMultiple from '../../Common/SelectionDropdownMultiple';
import TopicCreate from '../../Syllabus/TopicCreate';
import { securedFetchSubjects, securedFetchTopicsByFilter } from '../../../services/SyllabusService';
import { ADMIN_USER_TYPE } from '../../../services/UserService';
import { navigateCallbackOptions } from '../../../services/AuthenticationService';
import { securedFetchGrades } from '../../../services/BatchService';

import { AiFillCloseCircle } from 'react-icons/ai';
import { MdAdd } from 'react-icons/md';
import TagCreate from '../../Tags/TagCreate';
import { securedFetchTagsByFilter } from '../../../services/TagService';
import { BsCloudUpload } from 'react-icons/bs';
import VideoUpload from '../../Files/Video/VideoUpload';
import VideoPreview from '../../Files/Video/VideoPreview';
import { RECORDED_VIDEO_UPLOAD_TYPES, securedCreateRecordedVideo, securedCreateVideo, securedUploadVideoFile } from '../../../services/VideoService';
import Spinner from '../../Common/Tailwind/Spinner';

Modal.setAppElement('#root'); // This is important for accessibility reasons

const RecordedVideoCreate = ({ isOpen = true, inputReadingMaterialData: inputRecordedVideoData = { id: null }, onRequestClose = null, onAddReadingMaterial: onAddRecordedVideo = null, user = ADMIN_USER_TYPE,
    initialSubjects = null, initialSelectedSubject = null, initialGrades = null, initialSelectedGrade = null, initialSelectedTopics = null, initialSelectedTags = null, initialSelectedType = null, allowedTypes = null, hiddenFilters = null }) => {

    const [videoName, setVideoName] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState({});
    const [grades, setGrades] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState({});
    const [types, setTypes] = useState(allowedTypes ? allowedTypes : RECORDED_VIDEO_UPLOAD_TYPES);
    const [selectedType, setSelectedType] = useState(allowedTypes ? allowedTypes[0] : RECORDED_VIDEO_UPLOAD_TYPES[0]);
    const [topics, setTopics] = useState([]);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [isTopicCreateModalOpen, setTopicCreateModalOpen] = useState(false);
    const [isTagCreateModalOpen, setTagCreateModalOpen] = useState(false);
    const [videos, setVideos] = useState([]);
    const [videoFileNames, setVideoFileNames] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const navigate = useNavigate();


    const resetFilters = async () => {
        setVideos([]);
        setVideoFileNames([]);
        setSelectedGrade({});
        setSelectedSubject({});
        const topicsResponse = await fetchAndSetTopics({ subject_ids: subjects.map(subject => subject.id), grades: grades.map(grade => grade.id) });
        if (topicsResponse === null) {
            return;
        }
        setTopics(topicsResponse);
        const tagsResponse = await securedFetchTagsByFilter(null, navigateCallbackOptions(navigate));
        if (tagsResponse === null) {
            return;
        }
        setTags(tagsResponse.data);
        setSelectedTopics([]);
        setVideoName('');
        setSelectedTags([]);
    }


    const makeTopicFilter = (initialFilter) => {
        const filter = {};
        if (selectedSubject && selectedSubject.id) {
            filter['subject_ids'] = [selectedSubject.id];
        } else {
            filter['subject_ids'] = subjects.map(subject => subject.id);
        }
        if (selectedGrade && selectedGrade.id) {
            filter['grades'] = [selectedGrade.id];
        } else {
            filter['grades'] = grades.map(subject => subject.id);
        }
        return filter;
    }

    const makeVideoData = (videoUrl) => {
        return {
            url: videoUrl,
            title: videoName
        };
    }

    const makeRecordedVideoData = (videoId) => {
        return {
            video_type: selectedType.id,
            video_id: videoId,
            title: videoName,
            topics: selectedTopics.map(selectedTopic => ({ topic_id: selectedTopic.id })),
            tags: selectedTags.map(selectedTag => ({ tag_id: selectedTag.id }))
        }
    }

    const isInternalDataInvalid = () => {
        return videoName.length === 0 && selectedTopics.length > 0;
    }

    const onUploadVideoSuccess = async (url) => {
        const data = makeVideoData(url);
        const videoCreateResponse = await securedCreateVideo(data, navigateCallbackOptions(navigate));
        if (videoCreateResponse === null) {
            return;
        }
        const recordedVideoData = makeRecordedVideoData(videoCreateResponse.video_id);
        const recordedVideoCreateResponse = await securedCreateRecordedVideo(recordedVideoData, navigateCallbackOptions(navigate));
        alert('Video uploading completed!');
    }

    const handleSubmit = async () => {
        if (isInternalDataInvalid()) {
            return;
        }
        setIsSaving(true);
        const videoResponse = await securedUploadVideoFile(videos[0], { title: videoName }, { onSuccess: onUploadVideoSuccess }, navigateCallbackOptions(navigate));
        await resetFilters();
        alert("You will be notified once the video upload is complete.")
        setIsSaving(false);
        if (onRequestClose != null) {
            onRequestClose();
        }
    }


    const fetchAndSetSubjects = async () => {
        const subjectsResponse = await securedFetchSubjects(navigateCallbackOptions(navigate));
        if (subjectsResponse === null) {
            return null;
        }
        setSubjects(subjectsResponse.data);
        return subjectsResponse.data;
    };

    const fetchAndSetTopics = async (filter = null) => {
        let topicFilter = filter;
        if (!topicFilter) {
            topicFilter = makeTopicFilter();
        }
        const topicsResponse = await securedFetchTopicsByFilter(topicFilter, navigateCallbackOptions(navigate));
        if (topicsResponse === null) {
            return null;
        }
        setTopics(topicsResponse.data);
        return topicsResponse.data;
    };

    const onTopicCreate = async (newTopicId) => {
        const topicsResponse = await fetchAndSetTopics();
        const topicToAdd = topicsResponse.find(topic => (topic.id === newTopicId));
        if (topicToAdd) {
            setSelectedTopics([...selectedTopics, topicToAdd]);
        }
    }

    const onTagCreate = async (newTagId) => {
        const tagsResponse = await securedFetchTagsByFilter(null);
        const tagsResponseData = tagsResponse.data;
        setTags(tagsResponseData);
        const tagsToAdd = tagsResponseData.find(tag => (tag.id === newTagId));
        if (tagsToAdd) {
            setSelectedTags([...selectedTags, tagsToAdd]);
        }
    }

    const fetchAndSetGrades = async () => {
        const gradesResponse = await securedFetchGrades(navigateCallbackOptions(navigate));
        if (gradesResponse === null) {
            return null;
        }
        setGrades(gradesResponse.data.map(grade => ({ id: grade.id, grade: grade.grade.toString() })));
        return gradesResponse.data;
    };

    useEffect(() => {
        (async () => {
            if (!isOpen) {
                return;
            }
            setIsLoading(true);
            let subjectsResponse = initialSubjects, gradesResponse = initialGrades;
            if (subjectsResponse && subjectsResponse.length > 0) {
                setSubjects(initialSubjects);
            } else {
                subjectsResponse = await fetchAndSetSubjects();
                if (subjectsResponse === null) {
                    return;
                }
            }
            if (allowedTypes) {
                setTypes(allowedTypes);
                setSelectedType(allowedTypes[0]);
            }
            if (gradesResponse && gradesResponse.length > 0) {
                setGrades(gradesResponse.map(grade => ({ id: grade.id, grade: grade.grade.toString() })));
            } else {
                gradesResponse = await fetchAndSetGrades();
                if (gradesResponse === null) {
                    return;
                }
            }

            const gradeIdsForTopics = initialSelectedGrade && initialSelectedGrade.id ? [initialSelectedGrade.id] : ((selectedGrade && selectedGrade.id) ? [selectedGrade.id] : gradesResponse.map(grade => grade.id));
            const subjectIdsForTopics = initialSelectedSubject && initialSelectedSubject.id ? [initialSelectedSubject.id] : ((selectedSubject && selectedSubject.id) ? [selectedSubject.id] : subjectsResponse.map(subject => subject.id));

            const topicsResponse = await fetchAndSetTopics({ subject_ids: subjectIdsForTopics, grades: gradeIdsForTopics });
            if (topicsResponse === null) {
                return;
            }
            setTopics(topicsResponse);
            const tagsResponse = await securedFetchTagsByFilter(null);
            if (tagsResponse === null) {
                return;
            }
            setTags(tagsResponse.data);
            if (initialSelectedSubject && initialSelectedSubject.id) {
                setSelectedSubject(initialSelectedSubject);
            }
            if (initialSelectedGrade && initialSelectedGrade.id) {
                setSelectedGrade(initialSelectedGrade);
            }
            const initialSelectedTopicIds = initialSelectedTopics && initialSelectedTopics.length > 0 ? initialSelectedTopics.map(topic => topic.id) : selectedTopics.map(topic => topic.id);
            setSelectedTopics(topicsResponse.filter(topic => initialSelectedTopicIds.includes(topic.id)));

            const initialSelectedTagIds = initialSelectedTags && initialSelectedTags.length > 0 ? initialSelectedTags.map(tag => tag.id) : selectedTags.map(tag => tag.id);
            setSelectedTags(tagsResponse.data.filter(tag => initialSelectedTagIds.includes(tag.id)));
            if (initialSelectedType && initialSelectedType.id) {
                setSelectedType(initialSelectedType);
            }
            setIsLoading(false);
        })();
    }, [isOpen]);

    const onSelectedSubjectChange = async (selection) => {
        const subjectId = selection ? selection.value : null;
        if (subjectId) {
            const topicFilter = makeTopicFilter();
            topicFilter["subject_ids"] = [subjectId]
            const topicsResponseData = await fetchAndSetTopics(topicFilter);
            if (topicsResponseData === null) {
                return;
            }
            setSelectedSubject({ id: subjectId, subject_name: selection.label });
        }
        setSelectedTopics([]);
    };

    const onSelectedGradeChange = async (selection) => {
        const gradeId = selection ? selection.value : null;
        if (gradeId) {
            const topicFilter = makeTopicFilter();
            topicFilter["grades"] = [gradeId]
            const topicsResponse = await fetchAndSetTopics(topicFilter);
            if (topicsResponse === null) {
                return;
            }
            setSelectedGrade({ id: gradeId, grade: gradeId.toString() });
        }
        setSelectedTopics([]);
    };

    const onSelectedTopicToggle = (selected) => {
        const selectedIds = selected.map(sel => sel.value);
        const selectedTopicsFromSelect = topics.filter(topic => selectedIds.includes(topic.id));
        setSelectedTopics(selectedTopicsFromSelect);
    };

    const onSelectedTagToggle = (selected) => {
        const selectedIds = selected.map(sel => sel.value);
        const selectedTagsFromSelect = tags.filter(tag => selectedIds.includes(tag.id));
        setSelectedTags(selectedTagsFromSelect);
    };

    const onTypeChange = (selection) => {
        setSelectedType({ id: selection.value, name: selection.label });
    };

    const onSubmit = (event) => {
        event.preventDefault();
    };

    const checkDisabled = (key) => {
        return hiddenFilters && hiddenFilters[key] === 'disable';
    }

    const renderRecordedVideoCreate = () => {
        if (isLoading) {
            return <Spinner />;
        }

        if (isSaving) {
            return <Spinner />;
        }

        return (
            <>
                <h3 className="modalTitle"> Create New Video Material </h3>

                <form className="recordedVideoContent flex" onSubmit={onSubmit} >
                    <div className="recordedVideoTextInput">
                        <label className="title">Name</label>
                        <textarea className="text" placeholder="Enter..." onChange={(e) => setVideoName(e.target.value)} value={videoName} rows="1" required />
                    </div>

                    <div className="recordedVideoInfoInput flex">
                        {(!hiddenFilters || (hiddenFilters.subjects !== 'hide' && hiddenFilters.topics !== 'hide')) && (<SelectionDropdown className="recordedVideoSelectionDropdown" name='Subject' onSelectionChange={onSelectedSubjectChange} itemList={subjects} selectedItem={selectedSubject} nameField='subject_name' valueField='id' isDisabled={checkDisabled('subjects')} />)}
                        {(!hiddenFilters || (hiddenFilters.grades !== 'hide' && hiddenFilters.topics !== 'hide')) && (<SelectionDropdown className="recordedVideoSelectionDropdown" name='Grade' onSelectionChange={onSelectedGradeChange} itemList={grades} selectedItem={selectedGrade} nameField='grade' valueField='id' isDisabled={checkDisabled('grades')} />)}
                        {(!hiddenFilters || (hiddenFilters.types !== 'hide')) && (<SelectionDropdown className="recordedVideoSelectionDropdown" name="Type" itemList={types} selectedItem={selectedType} onSelectionChange={onTypeChange} nameField='name' valueField='id' isDisabled={checkDisabled('types')} />)}
                    </div>
                    {(!hiddenFilters || (hiddenFilters.topics !== 'hide')) && (
                        <div className="recordedVideoTopicInput flex">
                            <SelectionDropdownMultiple className="recordedVideoSelectionDropdownMultiple" name='Topics' itemList={topics} selectedItems={selectedTopics} onSelectionToggle={onSelectedTopicToggle} nameField='topic_name' valueField='id' isSearchable={true} isDisabled={checkDisabled('topics')} required />
                            <button id="add-btn" onClick={() => setTopicCreateModalOpen(true)}><MdAdd className="icon" /> </button>
                        </div>)}

                    {(!hiddenFilters || (hiddenFilters.tags !== 'hide')) && (
                        <div className="recordedVideoTagInput flex">
                            <SelectionDropdownMultiple className="recordedVideoSelectionDropdownMultiple" name='Tags' itemList={tags} selectedItems={selectedTags} onSelectionToggle={onSelectedTagToggle} nameField='tag_name' valueField='id' isSearchable={true} isDisabled={checkDisabled('tags')} />
                            <button id="add-btn" onClick={() => setTagCreateModalOpen(true)}><MdAdd className="icon" /> </button>
                        </div>)}

                    <VideoUpload title='Video' videos={videos} setVideos={setVideos} label={`video`} maxSize={1} setVideoFileNames={setVideoFileNames} />
                    <VideoPreview videos={videos} setVideos={setVideos} videoFileNames={videoFileNames} />

                    <button className='btn flex' id='submit' onClick={handleSubmit}>
                        SUBMIT <BsCloudUpload className="icon" />
                    </button>
                </form>

                <TopicCreate isOpen={isTopicCreateModalOpen} onRequestClose={() => setTopicCreateModalOpen(false)} initialSubjects={subjects} initialGrades={grades} initialSubject={selectedSubject} initialGrade={selectedGrade} onTopicCreate={onTopicCreate} user={user} />
                <TagCreate isOpen={isTagCreateModalOpen} onRequestClose={() => setTagCreateModalOpen(false)} allTags={tags} onTagCreate={onTagCreate} user={user} />

                <AiFillCloseCircle className="icon" id='close' onClick={onRequestClose} />
            </>
        )
    }

    return (

        <Modal overlayClassName="customModal" className="recordedVideoModal flex" isOpen={isOpen} onRequestClose={onRequestClose} >
            {renderRecordedVideoCreate()}
        </Modal>

    );
}



export default RecordedVideoCreate;